import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { catchError, take, withLatestFrom } from 'rxjs/operators';
import { SearchInputColorEnum } from 'src/app/shared/components/search-input/search-input.component';
import { WEB_SOCKET_DISCONNECTED_VALUE } from 'src/app/shared/constants/constants';
import { isSubstringOfElement } from 'src/app/shared/helpers/helpers';
import { BadgeColorsEnum } from 'src/app/shared/interfaces/BadgeColorsEnum';
import { IUnnecessaryToConfirmOwedPendingTransactionDialogState } from 'src/app/shared/interfaces/IUnnecessaryToConfirmOwedPendingTransactionDialogState';
import { IPendingTransaction } from 'src/app/shared/interfaces/pending-transaction.interface';
import { NavigatedFromPipe } from 'src/app/shared/pipes/navigated-from/navigated-from.pipe';
import { PendingTransactionsHubService } from 'src/app/shared/services/pending-transactions-hub.service';
import { GetPharmacyManagementSystems } from 'src/app/shared/states/profile/profile.actions';
import { ProfileState } from 'src/app/shared/states/profile/profile.state';
import { CloseNoNeedToConfirmOwedDialogAndRemoveCard, GetPendingTransactionDetails, GetPendingTransactions, SetCarouselDisplayList } from 'src/app/shared/states/transactions/transactions.actions';
import { TransactionsState } from 'src/app/shared/states/transactions/transactions.state';

@UntilDestroy()
@Component({
  selector: 'app-pending-transactions-carousel',
  templateUrl: './pending-transactions-carousel.component.html',
  styleUrls: ['./pending-transactions-carousel.component.scss'],
})
export class PendingTransactionsCarouselComponent implements OnInit {
  
  @Select(TransactionsState.pendingTransactions) pendingTransactions$: Observable<IPendingTransaction[]>;
  @Select(TransactionsState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(TransactionsState.unnecessaryToConfirmOwedPendingTransactionDialogState) unnecessaryToConfirmOwedPendingTransactionDialogState$: Observable<IUnnecessaryToConfirmOwedPendingTransactionDialogState>;
  @Select(TransactionsState.bulkVerifyInProgress) bulkVerifyInProgress$: Observable<boolean | undefined>;

  pendingTransactionsSearchControl = new FormControl();
  showConfirmationDialog : boolean;

  TRANSACTIONS_DISPLAY_COUNT = 5;
  CARD_MIN_WIDTH = 290;
  
  startIndex = 0; // display transactions from index
  lastIndex = this.TRANSACTIONS_DISPLAY_COUNT - 1;

  mediator = []; //items to display in carousel

  BadgeColorsEnum = BadgeColorsEnum;

  filteredTransactions: IPendingTransaction[]  = []

  showNoValidTransactionError: boolean;

  SearchInputColorEnum = SearchInputColorEnum;

  isSearchShrunk: boolean = true;

  searchValue: string = '';

  @ViewChild('cardsContainer') cardsContainer;

  navigation: any;
  navigationState: any;
  
  constructor(
    private store: Store, 
    private pendingTransactionsHubService: PendingTransactionsHubService,
    private router: Router,
    ) {
      this.store.dispatch(new GetPharmacyManagementSystems())
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const isAnyActive = this.store.selectSnapshot(ProfileState.pharmacyManagementSystemIntegrations)?.some(x => x.isActive);
        if(isAnyActive && (!pendingTransactionsHubService?.getState() || pendingTransactionsHubService?.getState() === WEB_SOCKET_DISCONNECTED_VALUE)) {
          this.pendingTransactionsHubService.startListening();
        }
      });
    this.navigation = this.router.getCurrentNavigation();
  }
  
  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.fitCardsWithScreenSize();
  }

  ngOnInit(): void {
    this.navigationState = this.navigation?.extras?.state;

    this.store.dispatch(new GetPendingTransactions())
      .pipe(take(1))
      .subscribe();

    this.pendingTransactions$
      .pipe(untilDestroyed(this))
      .subscribe((pendingTransactions) => {
        if (!!this.searchValue) {
          this.searchFilter(this.searchValue);
        } else {
          this.filteredTransactions = pendingTransactions;
          setTimeout(() => {
            this.fitCardsWithScreenSize();
          }, 0);
        }
      }); 

      this.pendingTransactionsSearchControl
      .valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(searchValue => this.searchFilter(searchValue));
  }

  searchFilter(searchValue: string) {
    this.searchValue = searchValue;
    searchValue = searchValue?.trim();
    this.filteredTransactions = searchValue ?
    this.store.selectSnapshot(TransactionsState)?.pendingTransactions.filter(el => {
      return (el.rxNumber.toLowerCase().includes(searchValue.toLowerCase())) ||
            (el.patientName.toLowerCase().includes(searchValue.toLowerCase())) ||
        (el.packageNdcs.find(el => isSubstringOfElement(el, searchValue)) || el.packageNdcsWithoutDash.find(el => isSubstringOfElement(el, searchValue)))
      }) : this.store.selectSnapshot(TransactionsState)?.pendingTransactions;

      this.populateDisplayListAfterFiltering();
  }

  back() {
    if (this.startIndex === 0) {
      this.startIndex = this.filteredTransactions.length - 1;
      this.lastIndex--;
      this.mediator.unshift(this.filteredTransactions[this.filteredTransactions.length - 1]);
      this.mediator.pop();
    }
    else if (this.lastIndex === 0) {
      this.lastIndex = this.filteredTransactions.length - 1;
      this.startIndex--;
      this.mediator.unshift(this.filteredTransactions[this.startIndex]);
      this.mediator.pop();
    }
    else {
      this.startIndex--;
      this.lastIndex--;
      this.mediator.unshift(this.filteredTransactions[this.startIndex]);
      this.mediator.pop();
    }
    this.store.dispatch(new SetCarouselDisplayList([...this.mediator || []]));
  }

  forward() { 
    if (this.lastIndex === this.filteredTransactions.length - 1) {
      this.lastIndex = 0;
      this.startIndex++;
      this.mediator.shift();
      this.mediator.push(this.filteredTransactions[0]);
    }
    else if (this.startIndex === this.filteredTransactions.length - 1) {
      this.startIndex = 0;
      this.lastIndex++;
      this.mediator.shift()
      this.mediator.push(this.filteredTransactions[this.lastIndex]);
    }
    else {
      this.startIndex++;
      this.lastIndex++;
      this.mediator.shift()
      this.mediator.push(this.filteredTransactions[this.lastIndex])
    }
    this.store.dispatch(new SetCarouselDisplayList([...this.mediator || []]));
  }

  openConfirmationDialog(id?: number) {
    id ??=this.store.selectSnapshot(TransactionsState).unconfirmedDispenseId;

    this.store.dispatch(new GetPendingTransactionDetails(id))
    .pipe(untilDestroyed(this), withLatestFrom(this.unnecessaryToConfirmOwedPendingTransactionDialogState$))
    .subscribe(([_, unnecessaryToConfirmOwedPendingTransactionDialogState]) => {
      if(!unnecessaryToConfirmOwedPendingTransactionDialogState && !this.store.selectSnapshot(TransactionsState).currentPendingTransaction) {
        this.showNoValidTransactionError = true;
      } else if (this.store.selectSnapshot(TransactionsState).currentPendingTransaction) {
        this.showConfirmationDialog = true;
      } 
    });
  }

  private populateDisplayList() {
    if (this.filteredTransactions?.length >= this.TRANSACTIONS_DISPLAY_COUNT) {

      this.mediator = this.store.selectSnapshot(TransactionsState)?.carouselDisplayList || [];

      if(this.mediator?.length) { //handle display array state
        let currentFutureDisplayListDiff = this.TRANSACTIONS_DISPLAY_COUNT - this.mediator.length;
        this.startIndex = this.filteredTransactions.findIndex(x => x.id === this.mediator[0]?.id);
        if(this.startIndex === -1 && this.filteredTransactions.length > 1) {
          this.startIndex = this.filteredTransactions.findIndex(x => x.id === this.mediator[1]?.id); // When first elemet is removed, the new first display item will be the second one
        }
        
        this.lastIndex = this.startIndex + this.mediator.length - 1;
         
        if(this.lastIndex + currentFutureDisplayListDiff > this.filteredTransactions.length - 1) { //case of screen resize
          this.lastIndex = this.filteredTransactions.length - this.startIndex + 1;
          this.mediator = Array.prototype.concat(
            this.filteredTransactions.slice(this.startIndex), 
            this.filteredTransactions.slice(0, this.TRANSACTIONS_DISPLAY_COUNT - (this.filteredTransactions.length - this.startIndex))
            )
        } else {
          this.mediator = this.filteredTransactions.slice(this.startIndex, this.lastIndex + currentFutureDisplayListDiff + 1);
        }
      }

      else this.mediator = this.filteredTransactions?.slice(0, this.TRANSACTIONS_DISPLAY_COUNT);
      
    } else {
      this.mediator = this.filteredTransactions?.slice(0);
    }
    
    this.store.dispatch(new SetCarouselDisplayList([...this.mediator || []]));
  }

  private populateDisplayListAfterFiltering(): void {
    this.mediator = this.filteredTransactions?.slice(0, this.TRANSACTIONS_DISPLAY_COUNT);
    this.startIndex = 0;
    this.lastIndex = this.mediator?.length - 1;
    this.store.dispatch(new SetCarouselDisplayList([...this.mediator || []]));
  }

  shouldHideBackForward(): boolean {
    return this.mediator.length < this.TRANSACTIONS_DISPLAY_COUNT;
  }

  reachedStart(): boolean {
    return this.mediator[0]?.id === this.filteredTransactions[0]?.id && !this.shouldHideBackForward();
  }

  reachedEnd(): boolean {
    return this.mediator[this.mediator.length - 1]?.id === this.filteredTransactions[this.filteredTransactions.length - 1]?.id && !this.shouldHideBackForward();
  }

  isFirstElement(id: number): boolean {
   return this.filteredTransactions[0]?.id === id ;
  }
  
  private fitCardsWithScreenSize() {
    const containerWidth = this.cardsContainer?.nativeElement?.clientWidth;
    if(containerWidth) {
      const cardsToFit = containerWidth / this.CARD_MIN_WIDTH + 1;
      this.TRANSACTIONS_DISPLAY_COUNT = Math.floor(cardsToFit);
      this.lastIndex = this.TRANSACTIONS_DISPLAY_COUNT - 1;
      this.populateDisplayList();
    }
  }

  closeNoNeedToConfirmCardDialog() {
    this.store.dispatch(new CloseNoNeedToConfirmOwedDialogAndRemoveCard());
  }

  navigateToBulkVerify(): void {
    this.router.navigateByUrl('/dashboard/bulk-verify');
  }

  searchInputBlurred(): void {
    if (!this.searchValue) {
      this.isSearchShrunk = true;
    }
  }

  searchCleared(): void {
    setTimeout(() => {
      this.isSearchShrunk = true
    });
  }

  onForwardClickedAndHeld(): void {
    if (!this.reachedEnd()) {
      this.forward();
    }
  }

  onBackClickedAndHeld(): void {
    if (!this.reachedStart()) {
      this.back();
    }
  }
}
